import { Analytics } from '@analytics';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';
import CustomSection from '../CustomSection';
import messages from './messages';

const useStyles = makeStyles({
  textField: {
    width: '100%',
    fontSize: '14px',
    backgroundColor: '#fff',

    '& .MuiInputBase-root': {
      color: '#000',
      fontWeight: 500
    }
  }
});

const initialCustomLink = {
  url: '',
  buttonText: ''
};

const getCustomLinkSchema = isCustomLinkEnabled =>
  yup.object({
    url: yup.string().when([], {
      is: () => isCustomLinkEnabled,
      then: schema =>
        schema
          .required('Custom Link is required')
          .matches(
            /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\/[^\s]*)?$/,
            'Invalid URL format'
          ),
      otherwise: schema => schema.notRequired()
    }),
    buttonText: yup.string().when([], {
      is: () => isCustomLinkEnabled,
      then: schema => schema.required('Button Text is required'),
      otherwise: schema => schema.notRequired()
    })
  });

const CustomLinkSection = ({
  companyId,
  externalLink,
  updateCompanyExternalLink,
  onRemove,
  onValuesChange,
  onToggle
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [isEnabled, setIsEnabled] = React.useState(externalLink?.enabled ?? false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsEnabled(externalLink?.enabled ?? false);
  }, [externalLink?.enabled]);

  const handleSave = values => {
    Analytics.track('Custom Link Saved', {
      page: 'setup'
    });
    // `_key` is a client-only React key — never persist it. Note `values`
    // retains `_id` for saved links so the backend updates in place instead
    // of creating a duplicate.
    const linkValues = { ...values };
    delete linkValues._key;
    updateCompanyExternalLink(companyId, {
      ...linkValues,
      ...(!externalLink._id && { enabled: true })
    });
  };

  const handleActiveToggle = async (_, isChecked) => {
    setIsEnabled(isChecked); // Optimistically update UI
    if (onToggle) onToggle(isChecked); // reflect enable/disable in the live preview
    setIsLoading(true);
    try {
      const linkValues = { ...externalLink };
      delete linkValues._key;
      await updateCompanyExternalLink(companyId, {
        ...linkValues,
        enabled: isChecked
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSectionBody = ({ errors, touched, values, handleChange, handleBlur }) => (
    <div className="flex flex-col gap-5 w-full">
      <TextField
        type="text"
        name="url"
        className={classes.textField}
        label={intl.formatMessage(messages.url)}
        variant="outlined"
        placeholder={intl.formatMessage(messages.url)}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.url}
        error={touched.url && Boolean(errors.url)}
        helperText={touched.url && errors.url}
      />
      <TextField
        type="text"
        name="buttonText"
        className={classes.textField}
        label={intl.formatMessage(messages.buttonText)}
        variant="outlined"
        placeholder={intl.formatMessage(messages.buttonText)}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.buttonText}
        error={touched.buttonText && Boolean(errors.buttonText)}
        helperText={touched.buttonText && errors.buttonText}
      />
    </div>
  );

  return (
    <CustomSection
      id="CustomLink"
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      renderBody={renderSectionBody}
      buttonTitle={intl.formatMessage(messages.save)}
      onSave={handleSave}
      onValuesChange={onValuesChange}
      schema={getCustomLinkSchema(isEnabled)}
      initialValues={{ ...initialCustomLink, ...(externalLink || {}) }}
      handleActiveToggle={handleActiveToggle}
      showActiveToggle={externalLink?._id}
      isLoading={isLoading}
      isEnabled={isEnabled}
      onRemove={onRemove}
    />
  );
};

export default CustomLinkSection;
